import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

export const Advertisement = ({
    site = 'TEL.AFL',
    adType = 'PREMIUM',
    advertDimensions,
    adIndex,
    takeOver,
    enableLazyload,
    enableDebug
}) => {
    const META_LONG_TITLE_KEY = 'longTitle';

    const metaLongTitleProperty = useMemo(() => {
        return (
            document.querySelector(`[property="${META_LONG_TITLE_KEY}"]`)
                ?.content || null
        );
    }, []);

    const isWebView = useMemo(() => {
        return (
            PULSE.core.url.getParam('webview') === 'true' ||
            window.IS_ARTICLE_API === true
        );
    }, []);

    const adTargetingGamblingValue = useMemo(
        () => !!PULSE.app?.common?.bettingVis?.isVisible,
        []
    );

    const isOver18 = useMemo(() => {
        try {
            return !!PULSE.app?.common?.siteSettings?.get().over18;
        } catch {
            return false;
        }
    }, []);

    const publisher = useMemo(() => {
        try {
            return PULSE.app.common.url.checkBaseRoute('aflw') ? 'aflw' : 'afl';
        } catch {
            return 'afl';
        }
    }, []);

    // Lock device and area once (area can be volatile if recomputed each render)
    const device = useMemo(() => {
        if (window.innerWidth > PULSE.app.measurements.tablet) {
            return 'desktop';
        }
        if (
            window.innerWidth < PULSE.app.measurements.tablet &&
            window.innerWidth > PULSE.app.measurements.phablet
        ) {
            return 'tablet';
        }
        return 'mobile';
    }, []);

    const _getSiteName = () => {
        if (!isWebView) {
            return site;
        }
        const configuredAndroidSite = PULSE?.adLib?.androidSite;
        const configuredIOSSite = PULSE?.adLib?.IOSSite;
        const isIOS = PULSE.app.common.deviceHelper.isIOSTouchScreen;
        const isAndroid = PULSE.app.common.deviceHelper.isAndroidTouchScreen;

        if (isIOS() && configuredIOSSite) {
            return configuredIOSSite;
        }
        if (isAndroid() && configuredAndroidSite) {
            return configuredAndroidSite;
        }
        return site;
    };

    const siteName = useMemo(() => _getSiteName(), [isWebView, site]);

    const area = useMemo(() => {
        // compute once and lock it
        if (isWebView) {
            return 'news/in_article';
        }
        const siteArea = document.querySelector(
            '[property="adverts:site-area"]'
        );
        if (siteArea) {
            return siteArea.getAttribute('content') || 'BLANK';
        }

        try {
            if (PULSE.app.common.url.contains('')) {
                return 'HOME';
            }
            if (PULSE.app.common.url.contains('news')) {
                return 'NEWS';
            }
            if (
                PULSE.app.common.url.contains('videos') ||
                PULSE.app.common.url.contains('video')
            ) {
                return 'VIDEOS';
            }
            if (PULSE.app.common.url.contains('ladder')) {
                return 'LADDER';
            }
            if (
                PULSE.app.common.url.contains('match-centre') ||
                PULSE.app.common.url.contains('match')
            ) {
                return 'MATCHES';
            }
            if (
                PULSE.app.common.url.contains('fixture', 2) ||
                PULSE.app.common.url.contains('fixtures', 2)
            ) {
                return 'FIXTURE';
            }
            if (
                PULSE.app.common.url.contains('players') ||
                PULSE.app.common.url.contains('teams') ||
                PULSE.app.common.url.contains('team')
            ) {
                return 'TEAMS';
            }
            if (PULSE.app.common.url.contains('injury-list', 2)) {
                return 'INJURY';
            }
        } catch (err) {
            // fallthrough
        }
        return 'BLANK';
    }, [isWebView]);

    // stable advert size — fix sorting and memoize
    const advertSize = useMemo(() => {
        if (!advertDimensions) {
            return '';
        }
        const dimensionsArray = advertDimensions
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        // sort by width descending (parseInt of width)
        const sorted = dimensionsArray.sort((a, b) => {
            const aw = parseInt(a.split('x')[0], 10) || 0;
            const bw = parseInt(b.split('x')[0], 10) || 0;
            return bw - aw;
        });
        const filtered = sorted.filter((d) => {
            const w = parseInt(d.split('x')[0], 10) || 0;
            return w < window.innerWidth;
        });
        return filtered.join(', ');
    }, [advertDimensions]);

    // Small debug marker
    // console.info('-------- ADVERTISEMENT (render) ----------- ');

    const { AdProvider, Ad } = window.ReactGPTAds;

    return window.ReactGPTAds ? (
        <AdProvider
            site={siteName}
            area={area}
            publisher={publisher}
            debug={enableDebug === 'true'}
            beforeInit={({ targeting }) => {
                if (enableDebug === 'true') {
                    console.info(
                        '[Ad] beforeInit called with area/device:',
                        area,
                        device
                    );
                }

                targeting.setTargeting('device', device);
                targeting.setTargeting('section', area);

                if (!isOver18) {
                    targeting.setTargeting('age', 'under18');
                    targeting.exclude('Alcoholic_Beverages');
                    targeting.exclude('Porn');
                    targeting.exclude('Casino');
                } else {
                    targeting.setTargeting('age', 'over18');
                }

                if (!isOver18 || !adTargetingGamblingValue) {
                    targeting.setTargeting('gambling', 'false');
                    targeting.exclude('Gambling');
                } else {
                    targeting.setTargeting('gambling', 'true');
                }

                if (isWebView) {
                    if (metaLongTitleProperty) {
                        targeting.setTargeting('title', metaLongTitleProperty);
                    }
                }
            }}
            afterInit={({ targeting, refresh }) => {
                if (enableDebug === 'true') {
                    console.info(
                        '[Ad] beforeInit called with area/device:',
                        area,
                        device
                    );
                }

                // set references so window.refreshAds points to latest refresh function
                window.refreshAds = refresh;

                if (enableDebug === 'true') {
                    window.googletag
                        .pubads()
                        .addEventListener('slotRenderEnded', (e) => {
                            const el = document.getElementById(
                                e.slot.getSlotElementId()
                            );

                            console.info(
                                'slotRenderEnded:',
                                e.slot.getSlotElementId(),
                                '| isEmpty:',
                                e.isEmpty,
                                '| size:',
                                e.size,
                                '| el display:',
                                el?.style.display
                            );
                        });
                }

                PULSE.app.common.siteSettings.subscribe(
                    ({ over18, displayWagering }) => {
                        targeting.replaceTargeting('age', [
                            over18 ? 'over18' : 'under18'
                        ]);
                        targeting.replaceTargeting('gambling', [
                            displayWagering ? 'true' : 'false'
                        ]);

                        window.refreshAds();
                    }
                );
            }}
        >
            <Ad
                adType={adType}
                size={advertSize}
                adIndex={adIndex}
                lazyload={enableLazyload === 'true'}
                takeoverAd={takeOver === 'true'}
                disabled={false}
            />
        </AdProvider>
    ) : (
        false
    );
};

Advertisement.propTypes = {
    site: PropTypes.string,
    area: PropTypes.string,
    adType: PropTypes.string,
    advertDimensions: PropTypes.string.isRequired,
    adIndex: PropTypes.number,
    enableLazyload: PropTypes.string,
    takeOver: PropTypes.string,
    hideBettingOdds: PropTypes.bool,
    under18: PropTypes.bool,
    noAlcohol: PropTypes.bool,
    noGambling: PropTypes.bool,
    enableDebug: PropTypes.string
};
